import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollAnimations';
import { Button } from '@/components/ui/button';
import { FileText, Calculator, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import LeadMagnetDialog from './LeadMagnetDialog';

export default function LeadMagnets() {
  const { ref, isVisible } = useScrollReveal();
  const [activeDialog, setActiveDialog] = useState<'checklist' | 'roi' | null>(null);

  const magnets = [
    {
      id: 'checklist' as const,
      icon: FileText,
      badge: 'For Schools',
      title: 'The Ultimate School Uniform Branding Checklist',
      description: 'Everything you need to design, source, and manage a professional uniform program — from fabric selection to logo placement.',
      features: ['Fabric quality guide', 'Logo placement best practices', 'Uniform policy template', 'Vendor evaluation checklist'],
      dialogTitle: 'Download the School Uniform Branding Checklist',
      dialogDescription: 'Get our comprehensive checklist + customizable uniform policy template — 100% free.',
      downloadUrl: '/resources/school-uniform-branding-checklist.pdf',
    },
    {
      id: 'roi' as const,
      icon: Calculator,
      badge: 'For Corporates',
      title: 'Corporate Branding ROI Calculator',
      description: 'Calculate how much your business saves by switching to bulk, quality-branded uniforms and merchandise.',
      features: ['Instant savings estimate', '3-year projection', 'Quality bonus calculation', 'Custom report option'],
      dialogTitle: 'Try the ROI Calculator',
      dialogDescription: 'Enter your details below to get started — it takes less than 30 seconds.',
      downloadUrl: '/resources/corporate-branding-roi-guide.pdf',
    },
  ];

  return (
    <>
      <section id="resources" className="py-24 bg-background">
        <div ref={ref} className="container mx-auto px-4">
          <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-primary font-semibold text-xs uppercase tracking-widest">Free Resources</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold font-display text-foreground">
              Tools & Resources to Elevate Your Brand
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Download our free guides and calculators — trusted by 50+ schools and 100+ corporates across Nigeria.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {magnets.map((magnet, i) => (
              <div
                key={magnet.id}
                className={`group relative bg-card border-2 border-border rounded-2xl p-8 hover:border-primary/40 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: isVisible ? `${i * 150}ms` : '0ms' }}
              >
                <div className="absolute top-4 right-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 px-3 py-1 rounded-full">
                    {magnet.badge}
                  </span>
                </div>

                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary transition-colors duration-300">
                  <magnet.icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>

                <h3 className="text-xl font-bold font-display text-foreground mb-3 pr-20">{magnet.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-5">{magnet.description}</p>

                <ul className="space-y-2 mb-6">
                  {magnet.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => setActiveDialog(magnet.id)}
                  className="w-full bg-primary hover:bg-primary-glow text-primary-foreground text-base py-5"
                >
                  Get It Free <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LeadMagnetDialog
        open={activeDialog === 'checklist'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        magnetType="checklist"
        title={magnets[0].dialogTitle}
        description={magnets[0].dialogDescription}
        downloadUrl={magnets[0].downloadUrl}
      />

      <LeadMagnetDialog
        open={activeDialog === 'roi'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        magnetType="roi-calculator"
        title={magnets[1].dialogTitle}
        description={magnets[1].dialogDescription}
        downloadUrl={magnets[1].downloadUrl}
      />
    </>
  );
}
